import React from "react";

type AudienceKind = "adult" | "teen" | "kid" | "teacher";
type QuizLevel = "beginner" | "intermediate" | "advanced";

export interface ThemeHeaderProps {
  title: string;
  level?: QuizLevel;
  audience?: AudienceKind;
  estimatedDurationLabel?: string;
  progressionPercent?: number;
  onStart?: () => void;
  onContinue?: () => void;
}

function badgeClass(extra?: string) {
  return (
    "inline-flex items-center gap-2 px-2.5 py-1 text-xs rounded-full border bg-white dark:bg-slate-900 dark:border-slate-700 " +
    (extra || "")
  );
}

function levelLabel(level?: QuizLevel) {
  if (level === "beginner") return "Niveau: debutant";
  if (level === "intermediate") return "Niveau: intermediaire";
  if (level === "advanced") return "Niveau: avance";
  return "Niveau: libre";
}

function audienceLabel(a?: AudienceKind) {
  if (a === "kid") return "Jeunes";
  if (a === "teen") return "Ados";
  if (a === "teacher") return "Enseignants";
  if (a === "adult") return "Adultes";
  return "Public: tous";
}

export function ThemeHeader({
  title,
  level,
  audience,
  estimatedDurationLabel,
  progressionPercent = 0,
  onStart,
  onContinue,
}: ThemeHeaderProps) {
  const showContinue = progressionPercent > 0;

  return (
    <section className="space-y-3 rounded-2xl border shadow-sm p-6 bg-white dark:bg-slate-900 dark:border-slate-700">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="space-y-1">
          <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Theme
          </p>
          <h1 className="text-2xl font-semibold">{title}</h1>
          <div className="flex flex-wrap gap-2">
            <span className={badgeClass()}>{levelLabel(level)}</span>
            <span className={badgeClass()}>{audienceLabel(audience)}</span>
            {estimatedDurationLabel && (
              <span className={badgeClass()}>Duree estimee: {estimatedDurationLabel}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex flex-col items-end">
            <span className="text-xs text-slate-500 dark:text-slate-400">Progression</span>
            <span className="text-lg font-semibold">{progressionPercent}%</span>
          </div>
          <div className="h-2 w-32 rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden">
            <div
              className="h-full bg-blue-500"
              style={{ width: `${Math.min(Math.max(progressionPercent, 0), 100)}%` }}
            />
          </div>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        <button
          type="button"
          onClick={showContinue ? onContinue : onStart}
          className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700"
        >
          {showContinue ? "Continuer" : "Commencer"}
        </button>
        <button
          type="button"
          className="inline-flex items-center justify-center px-4 py-2 rounded-lg border text-sm font-semibold bg-white dark:bg-slate-900 dark:border-slate-700"
        >
          Voir le parcours
        </button>
      </div>
    </section>
  );
}
